import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, RefreshCw, Zap, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  linkedin: '#0a66c2', twitter: '#1da1f2', facebook: '#1877f2',
  instagram: '#e4405f', blog: '#ff6b35',
};

const STATUS_BG = {
  empty: 'rgba(255,255,255,0.03)',
  draft: 'rgba(240,194,127,0.1)',
  scheduled: 'rgba(124,94,250,0.1)',
  published: 'rgba(61,255,160,0.1)',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const POSTING_TIMES = ['06:00','07:00','07:30','08:00','09:00','10:00','12:00','14:00','17:00','18:00','19:00'];
const ALL_PLATFORMS = ['linkedin', 'blog', 'twitter', 'facebook', 'instagram'];

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export default function ContentCalendar() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [postingTime, setPostingTime] = useState('07:30');
  const [platforms, setPlatforms] = useState(['linkedin', 'blog', 'twitter', 'facebook']);

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekStartStr = weekStart.toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];

  const loadWeek = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/calendar/week`, { params: { week_start: weekStartStr } });
      setSlots(data.slots || []);
    } catch { setSlots([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadWeek(); }, [weekStartStr]);

  const generateCalendar = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/api/calendar/generate`, {
        week_start_date: weekStartStr,
        platforms,
        default_posting_time: postingTime,
        total_posts_per_week: platforms.length * 2,
      });
      await loadWeek();
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const draftAdvance = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/api/calendar/auto-draft`, { max_slots: 6, days_ahead: 3, user_id: 'user' });
      await loadWeek();
    } catch { }
    finally { setGenerating(false); }
  };

  const slotsForDay = (date) => {
    const ds = date.toISOString().split('T')[0];
    return slots.filter(s => s.date === ds).sort((a, b) => a.time.localeCompare(b.time));
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0 flex-wrap gap-y-2"
        style={{ background: 'rgba(10,10,20,0.95)', borderBottom: '1px solid var(--llmc-border)' }}>
        <Calendar size={22} style={{ color: '#7c5efa', flexShrink: 0 }} />
        <div>
          <div className="font-black text-white text-sm">Content Calendar</div>
          <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Weekly schedule · Default 7:30 AM ET · 3-day advance</div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select value={postingTime} onChange={e => setPostingTime(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-lg" data-testid="posting-time-select"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--llmc-text)' }}>
            {POSTING_TIMES.map(t => <option key={t} value={t}>{t} ET</option>)}
          </select>
          <button onClick={draftAdvance} disabled={generating} data-testid="draft-advance-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{ background: 'rgba(0,212,184,0.1)', border: '1px solid rgba(0,212,184,0.3)', color: '#00d4b8' }}>
            <Zap size={12} />Draft 3 Days
          </button>
          <button onClick={generateCalendar} disabled={generating} data-testid="generate-calendar-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #7c5efa, #5a3fd4)' }}>
            {generating
              ? <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />
              : <Zap size={12} />}
            Generate Week
          </button>
          <button onClick={loadWeek} disabled={loading} className="p-1.5 rounded-lg transition-opacity hover:opacity-80" style={{ opacity: 0.5, color: 'var(--llmc-text-muted)' }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Platform filter */}
      <div className="flex items-center gap-1.5 px-4 py-2 flex-shrink-0 flex-wrap"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <span className="text-xs mr-1" style={{ color: 'var(--llmc-text-muted)' }}>Platforms:</span>
        {ALL_PLATFORMS.map(p => {
          const active = platforms.includes(p);
          const color = PLATFORM_COLORS[p];
          return (
            <button key={p} onClick={() => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
              className="text-[10px] font-semibold px-2 py-1 rounded-full capitalize transition-all"
              style={{
                background: active ? `${color}22` : 'transparent',
                border: `1px solid ${active ? color + '88' : 'rgba(255,255,255,0.1)'}`,
                color: active ? color : 'rgba(255,255,255,0.4)',
              }}>
              {p}
            </button>
          );
        })}
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <button onClick={() => setWeekStart(prev => addDays(prev, -7))} className="p-1 rounded-lg llmc-btn-ghost">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-white min-w-[220px] text-center">
          {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} —{' '}
          {addDays(weekStart, 6).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </span>
        <button onClick={() => setWeekStart(prev => addDays(prev, 7))} className="p-1 rounded-lg llmc-btn-ghost">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setWeekStart(getMonday(new Date()))} className="text-xs px-2 py-1 rounded-lg llmc-btn-ghost">Today</button>
        <div className="ml-auto flex gap-1.5">
          {[{ label: '80% Primary', color: '#7c5efa' }, { label: '20% Secondary', color: '#00d4b8' }].map(({ label, color }) => (
            <span key={label} className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: `${color}18`, border: `1px solid ${color}40`, color }}>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7c5efa', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)', minHeight: 500 }}>
            {weekDates.map((date, i) => {
              const ds = date.toISOString().split('T')[0];
              const isToday = ds === today;
              const daySlots = slotsForDay(date);
              return (
                <div key={i} className="flex flex-col gap-1.5">
                  {/* Day header */}
                  <div className="text-center py-1.5 rounded-lg"
                    style={{
                      background: isToday ? 'rgba(124,94,250,0.15)' : 'transparent',
                      border: isToday ? '1px solid rgba(124,94,250,0.3)' : '1px solid transparent',
                    }}>
                    <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{DAYS[i]}</div>
                    <div className="text-sm font-bold" style={{ color: isToday ? '#a68eff' : 'var(--llmc-text)' }}>{date.getDate()}</div>
                  </div>

                  {/* Slots */}
                  {daySlots.length > 0 ? daySlots.map(slot => {
                    const color = PLATFORM_COLORS[slot.platform] || '#666';
                    return (
                      <div key={slot.id}
                        className="p-1.5 rounded-lg cursor-pointer transition-all text-left"
                        data-testid={`slot-${slot.id}`}
                        style={{
                          background: STATUS_BG[slot.status] || STATUS_BG.empty,
                          border: `1px solid ${color}44`,
                          borderLeft: `3px solid ${color}`,
                        }}
                        title={`${slot.platform.toUpperCase()} · ${slot.topic} · ${slot.time} ET`}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[9px] font-bold" style={{ color }}>{slot.platform.slice(0, 3).toUpperCase()}</span>
                          <div className="ml-auto flex items-center gap-0.5">
                            <Clock size={8} style={{ color: 'rgba(255,255,255,0.3)' }} />
                            <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{slot.time}</span>
                          </div>
                        </div>
                        <div className="text-[10px] leading-tight" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          {(slot.title || slot.topic || '').slice(0, 30)}
                        </div>
                        <div className="mt-0.5 inline-block text-[8px] font-bold px-1 rounded"
                          style={{
                            background: slot.topic_priority === 'primary' ? 'rgba(124,94,250,0.2)' : 'rgba(0,212,184,0.15)',
                            color: slot.topic_priority === 'primary' ? '#a68eff' : '#00d4b8',
                          }}>
                          {slot.topic_priority}
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="rounded-lg border border-dashed flex items-center justify-center"
                      style={{ minHeight: 80, borderColor: 'rgba(255,255,255,0.06)' }}>
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.15)' }}>Empty</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stats */}
        {slots.length > 0 && (
          <div className="mt-3 p-3 rounded-xl flex gap-5 flex-wrap"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
            <div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>Total Slots</div>
              <div className="text-lg font-bold" style={{ color: '#a68eff' }}>{slots.length}</div>
            </div>
            {['linkedin', 'blog', 'twitter', 'facebook'].map(p => {
              const count = slots.filter(s => s.platform === p).length;
              if (!count) return null;
              return (
                <div key={p}>
                  <div className="text-[10px] capitalize" style={{ color: 'var(--llmc-text-muted)' }}>{p}</div>
                  <div className="text-lg font-bold" style={{ color: PLATFORM_COLORS[p] }}>{count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
